/**
 * Command application. The single write-door into the simulation.
 */

import { Cmd, isDebugCommand, type Command } from './commands.ts';
import {
  canAfford,
  Phase,
  RESOURCE_KEYS,
  RESOURCE_LABELS,
  spend,
  TAX_BANDS,
  Team,
  Terrain,
  TERRAIN_NAMES,
  WallKind,
  type ResourceBundle,
  type ResourceKey,
} from './types.ts';
import { RUBBLE_CLEAR_SECONDS } from './constants.ts';
import { EV, LogTone, pushEvent, pushLog } from './events.ts';
import {
  clearWall,
  findCompany,
  placeBuilding,
  recomputeManaCap,
  recomputePopCap,
  recomputeStorage,
  removeBuilding,
  setWall,
  spawnUnit,
  statsOf,
  type World,
} from './world.ts';
import {
  BUILDING_TYPES,
  buildingType,
  GATE_DEF,
  MAX_WALL_TIER,
  BRIDGE_DEF,
  MOAT_DEF,
  TOWER_DEF,
  WALL_DEF,
  wallTierDef,
} from '../data/buildings.ts';
import { TOWER_SIZE, isCurtain } from './systems/curtain.ts';
import { garrisonCompany, standDown } from './systems/garrison.ts';
import { unitType, UNIT_TYPES } from '../data/units.ts';
import { power, POWERS } from '../data/powers.ts';
import { castPower, unleashPower } from './systems/powers.ts';
import {
  awardHeroXp,
  buyHeroSkill,
  heroAbilityError,
  heroUnit,
  pickHero,
  reviveHero,
  HERO_ABILITY_COOLDOWN_MUL,
  HERO_ABILITY_RANGE,
} from './systems/hero.ts';
import {
  equipItem,
  foldPermanent,
  grantItem,
  reforgeItem,
  scrapItem,
  unequipSlot,
} from './systems/items.ts';
import {
  createCompany,
  fillCompany,
  orderCompany,
  recruitCompany,
  reinforceCompany,
} from './systems/companies.ts';
import { grantCapped, storageRoom } from './systems/settlement.ts';
import { raiseTown } from './systems/aitown.ts';
import { wreckTown } from './systems/sack.ts';
import { removeNode } from './systems/nodes.ts';
import { nodeKindById } from '../data/nodes.ts';
import { raceBuildRefusal } from './systems/bindings.ts';
import { beginSiege, forceChronicle, forceWave, planNextWave } from './systems/director.ts';
import { beginGuidedRun, endGuidedRun } from './systems/tutorial.ts';
import { setDate } from './systems/calendar.ts';
import { chronicleEvent } from '../data/events.ts';
import { applyLoadout, beginBattle, clampToDeployZone, loadoutError, LoadoutAct } from './battle.ts';
import type { Company } from './world.ts';
import { RELICS } from '../data/relics.ts';
import { TECH_NODES, techNode } from '../data/tech.ts';
import { isRaceId, raceEconomyMask } from '../data/races.ts';
import { defaultLeaderFor, leader } from '../data/leaders.ts';
import { ageCost, ageHearthTier, ageWaveFloor, MAX_AGE } from '../data/ages.ts';
import { MAX_UNIT_RANK, RANK_NUMERAL, rankAgeRequired, rankCost, rankHpMul } from '../data/ranks.ts';
import { AGE_NAMES } from './systems/director.ts';
import { applyCampaignCommand, isCampaignCommand } from './campaign/commands.ts';

export function applyCommands(w: World, cmds: readonly Command[]): void {
  for (const c of cmds) applyCommand(w, c);
}


export function applyCommand(w: World, c: Command): void {
  /**
   * The hold is exempt from both gates below, and has to be.
   *
   * It is not an order — it is the statement that this world is not currently
   * being played, because the player is somewhere else fighting. A world that
   * had a Chronicle question up when the player left for a battle would
   * otherwise refuse to be held, resume the moment the question was answered,
   * and quietly run a settlement through a fight it was supposed to be sitting
   * out. A world whose run has ended accepts it too: it costs nothing, and
   * refusing means a released world can be left holding after a load.
   */
  if (c.k === Cmd.SetBattleHold) {
    w.battleHold = c.a > 0;
    return;
  }
  if (c.k === Cmd.AttackCompany) {
    const mine = findCompany(w, c.a);
    if (mine && !mine.dead) {
      const foe = c.b >= 0 ? findCompany(w, c.b) : null;
      // Only an enemy is a target, and only a living one. Anything else is
      // read as "stop chasing and fight what is in front of you".
      mine.focusCompany = foe && !foe.dead && foe.team !== mine.team ? foe.id : -1;
    }
    return;
  }
  if (c.k === Cmd.SetCapitalStanding) {
    // The province travels with the order. A room has a capital per crown,
    // and "mine" is a different province on every client — reading it here
    // would have each machine improve a different city.
    const prov = w.campaign?.provinces[c.c];
    if (prov) {
      prov.development = c.a / 1000;
      prov.fort = Math.max(prov.fort, c.b);
    }
    return;
  }
  if (w.gameOver && !isDebugCommand(c.k)) return;
  /**
   * A pending Chronicle event is a hard stop, and it has to be enforced here
   * rather than in the UI. `stepWorld` already refuses to advance while one is
   * set; if orders still went through, a click queued behind the modal would
   * land in a frozen world, and a replay whose modal happened to be dismissed a
   * frame earlier would apply it to a different one.
   */
  if (w.pendingEvent && c.k !== Cmd.ResolveEvent && !isDebugCommand(c.k)) return;

  // The world map owns 40–62, trade owns 93–98, the sea owns 99–104 and holdings
  // own 105–108. Four ranges rather than one, because everything between them
  // belongs to somebody else. How many there are is `isCampaignCommand`'s
  // business, not this file's — which is what made the fourth block a one-line
  // change in one file.
  if (isCampaignCommand(c.k)) {
    applyCampaignCommand(w, c);
    return;
  }

  switch (c.k) {
    case Cmd.Build:
      doBuild(w, c.a, c.b, c.c);
      break;
    case Cmd.Demolish:
      doDemolish(w, c.a, c.b);
      break;
    case Cmd.WallLine:
      doWallLine(w, c.a, c.b, c.c, c.d, c.s === 'gate');
      break;
    case Cmd.RoadLine:
      doRoadLine(w, c.a, c.b, c.c, c.d);
      break;
    case Cmd.RebuildRubble:
      doRebuildRubble(w, c.a, c.b);
      break;
    case Cmd.ClearRubble:
      doClearRubble(w, c.a, c.b);
      break;
    case Cmd.OrderRepair:
      doOrderRepair(w, c.a, c.b > 0);
      break;
    /**
     * A holding that has just changed hands, arriving as what it is.
     *
     * Off `w.rng` — the world's own stream, the one every other seeded decision
     * in this settlement comes off — so the sack is part of the replay rather
     * than something that happened to the world before the log started. `a` is
     * permille, because a command carries integers.
     */
    case Cmd.WreckTown:
      wreckTown(w, w.rng, Math.max(0, Math.min(1000, c.a)) / 1000);
      break;
    case Cmd.DebugClearNodes:
      doClearNodes(w, c.a, c.b, c.c, c.s);
      break;
    case Cmd.RaiseTown: {
      /**
       * Somebody else's town, on this ground.
       *
       * Off `w.rng` — the world's own stream — so a replay that contains this
       * command rebuilds the identical town, and so the roll is part of the
       * same sequence every other draw in the run comes out of. The people are
       * the world's own, which is what a debug door should give you: the town
       * of whoever you are playing, to look at.
       */
      const rep = raiseTown(w, w.rng, {
        cx: c.a,
        cy: c.b,
        team: Team.Invader,
        tier: Math.max(1, Math.min(10, c.c || 5)),
        fort: Math.max(0, Math.min(3, c.d)),
        capital: c.c >= 9,
        raceId: w.raceId,
      });
      pushLog(
        w.events,
        `A town stands there: ${rep.buildings} roofs, ${rep.wallTiles} of wall, ${rep.gates} gate tiles.`,
        LogTone.Neutral,
      );
      break;
    }
    /**
     * The lessons, on and off. Both are one line each because the whole of the
     * guided run is one number the director reads — see `systems/tutorial.ts`.
     * `planNextWave` is re-run on begin because the first wave is usually
     * already planned by the time the menu's command lands (`stepPhase` plans
     * on the first tick of a fresh world), and a lesson that arrived a tick too
     * late to bend its own wave would be a card describing a wave that is not
     * coming.
     */
    case Cmd.TutorialBegin:
      if (beginGuidedRun(w, c.a || 1)) planNextWave(w);
      break;
    case Cmd.TutorialSkip:
      endGuidedRun(w);
      break;
    case Cmd.StartWave:
      if (w.battle) {
        beginBattle(w);
      } else if (w.config.homeCity) {
        // A capital cannot summon its own besiegers. The horn is the strategic
        // map's to sound.
        pushLog(w.events, 'There is nobody at the gates to call in. Yet.', LogTone.Neutral);
      } else if (w.phase === Phase.Build) {
        w.phaseT = 0;
        beginSiege(w);
      }
      break;
    case Cmd.MoveCompany: {
      const comp = findCompany(w, c.a);
      if (!comp || comp.team !== Team.Player || comp.dead) break;
      if (w.battle?.deploying) {
        // Deployment is placement, not marching: the company simply *is* there.
        const p = clampToDeployZone(w.battle, c.b, c.c);
        teleportCompany(w, comp, p.x, p.y);
      } else {
        /**
         * Being told to go somewhere is how a garrison comes down.
         *
         * Without this, a company that had been put on the wall would take the
         * order, march its banner off, and leave every man pinned to his stone
         * behind it — the order would appear to be ignored. A move order is the
         * plainest possible statement that the player wants them somewhere else.
         */
        if (comp.garrisonTile >= 0) standDown(w, comp);
        orderCompany(w, comp, c.b, c.c, c.d === 1);
      }
      break;
    }
    case Cmd.SetFormation: {
      const comp = findCompany(w, c.a);
      if (comp && comp.team === Team.Player) {
        comp.formation = c.b;
        comp.formationWidth = 0;
      }
      break;
    }
    case Cmd.SetFacing: {
      const comp = findCompany(w, c.a);
      if (comp && comp.team === Team.Player) {
        comp.facing = c.b;
        comp.formationWidth = Math.max(0, Math.round(c.c));
      }
      break;
    }
    case Cmd.SetStance: {
      const comp = findCompany(w, c.a);
      if (comp && comp.team === Team.Player) comp.stance = c.b;
      break;
    }
    case Cmd.BattleLoadout: {
      // The one door the setup board, the console and the headless harness all
      // speak — which is the whole reason the picks are a command and not a
      // construction handed to `createBattle` and forgotten.
      if (!applyLoadout(w, c.a, c.b)) {
        const b = w.battle;
        if (b && b.deploying && c.a === LoadoutAct.Add) {
          const why = loadoutError(w, b.loadout, c.b);
          if (why !== '') pushLog(w.events, why, LogTone.Neutral);
        }
      }
      break;
    }
    case Cmd.CastPower:
      castPower(w, c.s, c.a, c.b, c.c, c.d);
      break;
    case Cmd.Recruit:
      doRecruit(w, c.a);
      break;
    case Cmd.Reinforce: {
      const comp = findCompany(w, c.a);
      if (comp && comp.team === Team.Player && !comp.dead) reinforceCompany(w, comp);
      break;
    }
    case Cmd.DisbandCompany: {
      const comp = findCompany(w, c.a);
      if (comp && comp.team === Team.Player) {
        for (const h of comp.members) {
          const i = w.units.resolve(h);
          if (i >= 0) w.units.kill(i);
        }
        comp.members.length = 0;
        comp.dead = true;
      }
      break;
    }
    case Cmd.UpgradeBuilding:
      doUpgradeBuilding(w, c.a);
      break;
    case Cmd.UpgradePower:
      doUpgradePower(w, c.s);
      break;
    case Cmd.PickRelic:
      doPickRelic(w, c.s);
      break;
    case Cmd.Research:
      doResearch(w, c.s);
      break;
    case Cmd.SetTaxPolicy: {
      const next = Math.max(0, Math.min(TAX_BANDS.length - 1, Math.round(c.a)));
      if (next === w.taxPolicy) break;
      w.taxPolicy = next;
      pushLog(w.events, `Tax set to ${TAX_BANDS[next].name}. ${TAX_BANDS[next].note}`, LogTone.Neutral);
      break;
    }
    case Cmd.Trade:
      doTrade(w, c.s, c.a);
      break;
    case Cmd.AdvanceAge:
      doAdvanceAge(w);
      break;
    case Cmd.UpgradeUnitRank:
      doUpgradeUnitRank(w, c.s);
      break;
    /**
     * Jump the ledger. Numbered 30 rather than into the 64+ debug block because
     * 30–31 are the calendar's reservation, which means it obeys the same two
     * gates every ordinary command obeys: it is refused after the run has ended
     * and while a Chronicle event is pending. That is the correct behaviour for
     * something that moves the season under a frozen world.
     */
    case Cmd.DebugSetDate:
      setDate(w, c.a);
      break;
    /**
     * Jump to an Age, for the harness. The Age *is* the gate — `canPlaceBuilding`
     * and `doRecruit` both read `w.age` and nothing else — so there is nothing
     * else to unlock, and clearing the research clock is what stops a jump
     * landing on top of a purchase already in flight.
     */
    case Cmd.PickKingdom:
      doPickKingdom(w, c.s);
      break;
    case Cmd.DebugSetRace:
      doSetRace(w, c.s, c.a !== 0);
      break;
    case Cmd.DebugSetAge: {
      w.age = Math.max(1, Math.min(MAX_AGE, Math.round(c.a)));
      w.researchingAge = 0;
      w.ageResearch = 0;
      break;
    }
    case Cmd.UpgradeWallLine:
      doUpgradeWallLine(w, c.a, c.b, c.c, c.d);
      break;
    case Cmd.WallTower:
      doWallTower(w, c.a, c.b);
      break;
    case Cmd.Garrison:
      doGarrison(w, c.a, c.b, c.c, c.d > 0);
      break;
    case Cmd.BridgeLine:
      doBridgeLine(w, c.a, c.b, c.c, c.d);
      break;
    case Cmd.MoatLine:
      doMoatLine(w, c.a, c.b, c.c, c.d);
      break;
    case Cmd.ResolveEvent:
      doResolveEvent(w, c.s, c.a);
      break;

    // ── Hero ─────────────────────────────────────────────────────────────
    // A class's starting passive and every bought skill fold into the bag and
    // never come out, exactly like a relic, so they go through the same door.
    case Cmd.PickHero:
      foldPermanent(w, () => pickHero(w, c.s));
      break;
    case Cmd.HeroSkill:
      foldPermanent(w, () => buyHeroSkill(w, c.s));
      break;
    case Cmd.HeroAbility:
      doHeroAbility(w, c.s, c.a, c.b, c.c, c.d);
      break;
    case Cmd.ReviveHero:
      reviveHero(w);
      break;

    // ── The cart ─────────────────────────────────────────────────────────
    // None of these go through `foldPermanent`: an item's contribution is
    // rebuilt from `modsBase` by `refoldEquipment`, which each of them calls.
    case Cmd.EquipItem:
      equipItem(w, c.a);
      break;
    case Cmd.UnequipItem:
      unequipSlot(w, c.a);
      break;
    case Cmd.ReforgeItem:
      reforgeItem(w, c.a);
      break;
    case Cmd.ScrapItem:
      scrapItem(w, c.a);
      break;
    case Cmd.DebugGrantItem:
      grantItem(w, c.s, Math.max(1, c.a));
      break;

    // ── Debug console ────────────────────────────────────────────────────
    case Cmd.DebugSpawn: {
      const idx = UNIT_TYPES.findIndex((t) => t.id === c.s);
      if (idx >= 0) {
        const want = Math.max(1, c.c);
        if (c.d === 1) {
          for (let n = 0; n < want; n++) {
            spawnUnit(w, idx, Team.Invader, c.a + (n % 5) * 0.5, c.b + ((n / 5) | 0) * 0.5);
          }
          break;
        }
        /**
         * Debug-spawned *allies* arrive as companies now.
         *
         * They used to be loose models with `companyId` of −1, which meant they
         * had no formation, no stance, no anchor and — since the walls landed —
         * no way to be given an order at all, because every order in the game
         * names a company. A tester could conjure a hundred archers and could
         * not put one of them on a wall. It costs nothing and it is the shape a
         * player's troops have always had.
         */
        let made = 0;
        while (made < want) {
          const comp = createCompany(w, idx, Team.Player, c.a + (made % 5) * 0.5, c.b);
          const n = fillCompany(w, comp, Math.min(comp.maxSize, want - made), c.a + (made % 5) * 0.5, c.b);
          if (n <= 0) break;
          made += n;
        }
      }
      break;
    }
    case Cmd.DebugGive: {
      const key = c.s as keyof ResourceBundle;
      if (key in w.res) w.res[key] += c.a;
      // `heroxp` is not a resource, but it is the one number a tester wants to
      // move most, and it goes through the same door everything else does.
      else if (c.s === 'heroxp') awardHeroXp(w, c.a);
      break;
    }
    case Cmd.DebugSetWave:
      forceWave(w, c.a);
      break;
    case Cmd.DebugKillAll: {
      const u = w.units;
      for (let i = 0; i < u.slots.high; i++) {
        if (u.slots.alive[i] === 0) continue;
        if (u.team[i] === Team.Invader) u.kill(i);
      }
      w.spawnQueue.length = 0;
      break;
    }
    case Cmd.DebugGod:
      if (w.hearth >= 0) w.buildings.hp[w.hearth] = w.buildings.maxHp[w.hearth];
      break;
    case Cmd.DebugEvent:
      forceChronicle(w, c.s);
      break;
    default:
      break;
  }
}

// ── Building ───────────────────────────────────────────────────────────────

export function buildCostFor(w: World, typeIdx: number): Partial<ResourceBundle> {
  const def = buildingType(typeIdx);
  // Both halves: the faction's standing discount, and `mods.buildCost`, which
  // the Masons' Guild and three relics have been quietly multiplying into a
  // value nobody read.
  const mul = (w.faction.bonuses.buildCost ?? 1) * w.mods.buildCost;
  const out: Partial<ResourceBundle> = {};
  for (const k of Object.keys(def.cost) as Array<keyof ResourceBundle>) {
    out[k] = Math.round((def.cost[k] ?? 0) * mul);
  }
  return out;
}

/**
 * Everything that forbids a building before any ground has been chosen — the
 * Age, the people, and whether it has been learned. Returns the sentence to
 * refuse with, or `''` if nothing standing objects.
 *
 * Split out of `canPlaceBuilding` rather than duplicated because the build menu
 * needs exactly these three refusals and has no tile to test against: it is
 * greying an entry, not answering a click. Sharing the function means sharing
 * the *wording* and the order of precedence, so the sentence on the greyed
 * button and the sentence at the moment of the click cannot drift apart. The
 * same shape as `postReason` in `merchants.ts` and the `…Refusal` pair in
 * `fleets.ts`: a sentence, not a boolean, because a boolean teaches nobody
 * anything.
 */
export function buildRefusal(w: World, typeIdx: number): string {
  const def = buildingType(typeIdx);
  if (def.tier > w.age) return 'Not yet this Age.';
  /**
   * The first of the mask's four gates, and the one that does the real work: a
   * people is never *offered* a building that makes what it cannot hold, so in
   * an ordinary run nothing ever produces a masked resource in the first place.
   *
   * Race first and unlock second, because the two refusals mean different things
   * and the player deserves the right one. "Not ours to build" is permanent and
   * "we have not learned it" is a thing to go and do.
   */
  if (def.race && def.race !== w.raceId) return 'Not ours to build.';
  if (def.researched && !w.unlockedBuildings.includes(def.id)) return 'Not learned. Ask the Academy.';
  /**
   * And what this people may not build because of what *binds* it, which is a
   * different refusal from "not ours" and deserves its own sentence: the Choir
   * is not being told the Hovel belongs to somebody else, it is being told that
   * a house would do nothing for it. Last of the four so that the Age and the
   * Academy — refusals that are about *when* — are said first.
   */
  const bound = raceBuildRefusal(w, typeIdx);
  if (bound !== '') return bound;
  return '';
}

export function canPlaceBuilding(w: World, typeIdx: number, tx: number, ty: number): string {
  const def = buildingType(typeIdx);
  const standing = buildRefusal(w, typeIdx);
  if (standing !== '') return standing;
  let terrainOk = !def.needsTerrain;
  for (let dy = 0; dy < def.h; dy++) {
    for (let dx = 0; dx < def.w; dx++) {
      const x = tx + dx;
      const y = ty + dy;
      if (!w.map.inBounds(x, y)) return 'Off the map.';
      if (!w.map.canBuild(x, y)) return 'Ground is taken.';
      if (def.needsTerrain && def.needsTerrain.includes(w.map.terrain[w.map.idx(x, y)])) terrainOk = true;
    }
  }
  if (!terrainOk) return `Needs ${def.needsTerrain?.map(terrainName).join(' or ')}.`;
  /**
   * On the bank, not in the water. A ring one tile wide around the footprint,
   * which is the smallest neighbourhood that can mean "beside" — anything wider
   * and a weir could stand a street back from a river it cannot see.
   */
  if (def.needsNearTerrain) {
    let near = false;
    for (let dy = -1; dy <= def.h && !near; dy++) {
      for (let dx = -1; dx <= def.w && !near; dx++) {
        const x = tx + dx;
        const y = ty + dy;
        if (!w.map.inBounds(x, y)) continue;
        if (def.needsNearTerrain.includes(w.map.terrain[w.map.idx(x, y)])) near = true;
      }
    }
    if (!near) return `Must stand beside ${def.needsNearTerrain.map(terrainName).join(' or ')}.`;
  }
  if (!canAfford(w.res, buildCostFor(w, typeIdx))) return 'Not enough materials.';
  return '';
}

/**
 * Reads off `TERRAIN_NAMES` rather than a second list.
 *
 * It used to be a literal array of eight, written when there were eight, and it
 * had already fallen a terrain behind — a refusal that says "Needs ground."
 * tells a player nothing, and the Wellspring is the one building in the game
 * whose whole difficulty is *where*.
 */
function terrainName(t: number): string {
  return (TERRAIN_NAMES[t] ?? 'ground').toLowerCase();
}

/**
 * Choose the kingdom, and with it the people and the person.
 *
 * Refused once the run is under way, and that is the design rather than a
 * safeguard: the two identity passive layers are folded into `w.mods` at
 * `initRun` and modifiers in this codebase are never divided back out — see the
 * items note in COORDINATION.md for why, and for the forty-swaps test that
 * exists because somebody tried. A settlement that changed its people mid-run
 * would carry both peoples' modifiers for ever.
 *
 * So the command is a *pre-run* decision, applied to a world that has not yet
 * started, and the selection screen is where it lives. `initRun` reads what this
 * wrote. If it lands late it says so and does nothing.
 */
function doPickKingdom(w: World, id: string): void {
  if (w.tick > 0 || w.wave > 0) {
    pushLog(w.events, 'That was decided before any of this.', LogTone.Bad);
    return;
  }
  // A kingdom id today, a bare RaceId until `mapsea`'s kingdoms exist. Kingdoms
  // resolve through the leader table because a kingdom *is* a house on a race.
  const house = leader(id);
  if (house) {
    w.raceId = house.raceId;
    w.leaderId = house.id;
    return;
  }
  if (!isRaceId(id)) return;
  w.raceId = id;
  w.leaderId = defaultLeaderFor(id).id;
}

/** The harness's door. See `Cmd.DebugSetRace` for why it does not re-fold. */
function doSetRace(w: World, id: string, resetHouse: boolean): void {
  if (!isRaceId(id)) return;
  w.raceId = id;
  if (resetHouse) w.leaderId = defaultLeaderFor(id).id;
  recomputeStorage(w);
  recomputeManaCap(w);
}

function doBuild(w: World, typeIdx: number, tx: number, ty: number): void {
  const err = canPlaceBuilding(w, typeIdx, tx, ty);
  if (err) return;
  spend(w.res, buildCostFor(w, typeIdx));
  placeBuilding(w, typeIdx, tx, ty, Team.Player, false);
  const def = buildingType(typeIdx);
  pushEvent(w.events, EV.Build, tx + def.w / 2, ty + def.h / 2, 0, typeIdx, Team.Player);
}

/** What a demolition gives back, as a share of what the thing cost to build. */
const DEMOLISH_REFUND = 0.4;

/**
 * Pay a refund back into the store.
 *
 * Two bugs lived here. The refund was computed for every key of `def.cost` and
 * then credited for exactly three of them — timber, stone and iron — so planks,
 * grain and coin were calculated and thrown away, silently, on every demolition
 * of anything that cost them. And the credit went in unclamped, which meant a
 * settlement with full sheds got a number that `stepProduction` deleted later in
 * the same tick. `grantCapped` is the helper the market already uses for the
 * mirror-image problem; returning what was lost lets us say so out loud, which
 * is the difference between a full granary and a bug.
 */
function payRefund(w: World, cost: Partial<ResourceBundle>, share: number): void {
  let lost = 0;
  for (const key of RESOURCE_KEYS) {
    const due = (cost[key] ?? 0) * share;
    if (due <= 0) continue;
    lost += due - grantCapped(w, key, due);
  }
  if (lost >= 1) {
    pushLog(w.events, 'Some of the salvage had nowhere to go. Build more storage.', LogTone.Bad);
  }
}

function doDemolish(w: World, tx: number, ty: number): void {
  if (!w.map.inBounds(tx, ty)) return;
  const k = w.map.idx(tx, ty);
  const b = w.map.building[k];
  if (b >= 0) {
    if (b === w.hearth) return;
    const def = buildingType(w.buildings.typeId[b]);
    // A half-built frame gives back half a frame's worth.
    payRefund(w, def.cost, DEMOLISH_REFUND * w.buildings.build[b]);
    removeBuilding(w, b);
    recomputePopCap(w);
    recomputeStorage(w);
    recomputeManaCap(w);
    return;
  }
  if (w.map.wall[k] !== WallKind.None) {
    const kind = w.map.wall[k];
    const def = kind === WallKind.Gate ? GATE_DEF : kind === WallKind.Tower ? TOWER_DEF : WALL_DEF;
    payRefund(w, def.cost, DEMOLISH_REFUND);
    clearWall(w, tx, ty);
  }
}

/**
 * What a tower costs, all four tiles of it. The faction wall discount applies:
 * a tower is curtain, not a building.
 */
export function towerCost(w: World): Partial<ResourceBundle> {
  const costMul = w.faction.id === 'karrun' ? 0.8 : w.faction.id === 'moonbound' ? 2 : 1;
  const tiles = TOWER_SIZE * TOWER_SIZE;
  const cost: Partial<ResourceBundle> = {};
  for (const key of Object.keys(TOWER_DEF.cost) as ResourceKey[]) {
    cost[key] = (TOWER_DEF.cost[key] ?? 0) * costMul * tiles;
  }
  return cost;
}

/**
 * Whether a tower may stand here, as a sentence or `''`.
 *
 * The same shape as `canPlaceBuilding`, and exported for the same reason: the
 * placement preview has to be able to say *no* using the identical rule the
 * command will apply, or the ghost is a lie. The rules are only two — it must
 * not overwrite something that is already a feature of the line, and it must be
 * part of the line.
 */
export function canPlaceTower(w: World, tx: number, ty: number): string {
  let onLine = false;
  for (let dy = 0; dy < TOWER_SIZE; dy++) {
    for (let dx = 0; dx < TOWER_SIZE; dx++) {
      const x = tx + dx;
      const y = ty + dy;
      if (!w.map.inBounds(x, y)) return 'off the edge of the map';
      const kind = w.map.wall[w.map.idx(x, y)];
      if (kind === WallKind.Gate || kind === WallKind.Tower) {
        return 'there is already something in the line there';
      }
      if (kind === WallKind.Wall) {
        onLine = true;
        continue;
      }
      if (!w.map.canBuild(x, y)) return 'a tower wants four clear tiles';
    }
  }
  if (onLine) return '';
  // Not standing on the line: is it at least against it?
  for (let dy = -1; dy <= TOWER_SIZE; dy++) {
    for (let dx = -1; dx <= TOWER_SIZE; dx++) {
      if (dx >= 0 && dy >= 0 && dx < TOWER_SIZE && dy < TOWER_SIZE) continue;
      const x = tx + dx;
      const y = ty + dy;
      if (!w.map.inBounds(x, y)) continue;
      if (isCurtain(w.map, w.map.idx(x, y))) return '';
    }
  }
  return 'a tower belongs in a wall, not on its own in a field';
}

/**
 * Raise a tower: a 2×2 block of curtain, standing *in* the line rather than
 * beside it.
 *
 * It is allowed to swallow plain wall — that is what "part of the curtain" means
 * — but not a gate and not another tower, and at least one of the four tiles has
 * to be wall already or touch wall, so a tower cannot be planted in a field.
 * Paid for and stamped all four tiles at once or not at all: a half-bought tower
 * is an L-shaped lump of stone nobody asked for.
 */
function doWallTower(w: World, tx: number, ty: number): void {
  const why = canPlaceTower(w, tx, ty);
  if (why) {
    pushLog(w.events, why, LogTone.Bad);
    return;
  }
  const hpMul = w.faction.bonuses.wallHp ?? 1;
  const cost = towerCost(w);
  if (!canAfford(w.res, cost)) {
    pushLog(w.events, 'Not enough for a tower.', LogTone.Bad);
    return;
  }
  spend(w.res, cost);

  for (let dy = 0; dy < TOWER_SIZE; dy++) {
    for (let dx = 0; dx < TOWER_SIZE; dx++) {
      setWall(w, tx + dx, ty + dy, WallKind.Tower, TOWER_DEF.hp * hpMul, 1);
    }
  }
  pushEvent(w.events, EV.Build, tx + TOWER_SIZE / 2, ty + TOWER_SIZE / 2, 2, 0, Team.Player);
  pushLog(w.events, 'A tower goes up. Somewhere to put the archers.', LogTone.Good);
}

/** Put a company on the wall, or take it off again. */
function doGarrison(w: World, companyId: number, tx: number, ty: number, off: boolean): void {
  const c = findCompany(w, companyId);
  if (!c || c.team !== Team.Player || c.dead) return;
  if (off) {
    if (c.garrisonTile < 0) return;
    standDown(w, c);
    pushLog(w.events, `${c.name} comes down off the wall.`, LogTone.Neutral);
    return;
  }
  if (garrisonCompany(w, c, tx, ty)) {
    pushLog(w.events, `${c.name} takes the wall.`, LogTone.Good);
  } else {
    pushLog(w.events, 'There is no wall there to man.', LogTone.Bad);
  }
}

/** Walls are placed as a drag-line; each tile is paid for and built separately. */
function doWallLine(w: World, x0: number, y0: number, x1: number, y1: number, gate: boolean): void {
  const def = gate ? GATE_DEF : WALL_DEF;
  const hpMul = w.faction.bonuses.wallHp ?? 1;
  const costMul = w.faction.id === 'karrun' ? 0.8 : w.faction.id === 'moonbound' ? 2 : 1;
  const tiles = wallLineTiles(x0, y0, x1, y1);

  for (const [tx, ty] of tiles) {
    if (!w.map.canBuild(tx, ty)) continue;
    const cost: Partial<ResourceBundle> = {};
    for (const k of Object.keys(def.cost) as Array<keyof ResourceBundle>) {
      cost[k] = (def.cost[k] ?? 0) * costMul;
    }
    if (!canAfford(w.res, cost)) break;
    spend(w.res, cost);
    setWall(w, tx, ty, gate ? WallKind.Gate : WallKind.Wall, def.hp * hpMul, 1);
    pushEvent(w.events, EV.Build, tx + 0.5, ty + 0.5, 1, gate ? 1 : 0, Team.Player);
  }
}

/** Bresenham, so a drag-line looks like the line the player dragged. */
export function lineTiles(x0: number, y0: number, x1: number, y1: number): Array<[number, number]> {
  const out: Array<[number, number]> = [];
  let x = Math.round(x0);
  let y = Math.round(y0);
  const ex = Math.round(x1);
  const ey = Math.round(y1);
  const dx = Math.abs(ex - x);
  const dy = -Math.abs(ey - y);
  const sx = x < ex ? 1 : -1;
  const sy = y < ey ? 1 : -1;
  let err = dx + dy;
  let guard = 0;
  for (;;) {
    out.push([x, y]);
    if ((x === ex && y === ey) || guard++ > 512) break;
    const e2 = 2 * err;
    if (e2 >= dy) {
      err += dy;
      x += sx;
    }
    if (e2 <= dx) {
      err += dx;
      y += sy;
    }
  }
  return out;
}

/**
 * The same line, but *connected*.
 *
 * Bresenham on a diagonal produces a staircase of tiles that touch only at their
 * corners — and a soldier's step is tested against the tile it lands in, so a
 * diagonal wall had a one-tile-wide door between every pair of stones. Hordes
 * walked through diagonal walls. They always have. It reads as a wall, it is
 * priced as a wall, and it stops nothing.
 *
 * Whenever the walk turns a corner, the tile that squares it off is inserted.
 * A diagonal wall now costs about twice a straight one of the same span, which
 * is honest — it *is* twice as much wall — and the ring the player drags is
 * genuinely closed, which is the whole of what "walls that connect" means.
 *
 * The horizontal-first tile is the one chosen, always, so the shape a drag
 * produces is a pure function of its endpoints. Roads and the moat still use the
 * thin `lineTiles`: a diagonal road that a man can walk is fine, because a road
 * is not trying to stop anybody.
 */
export function wallLineTiles(x0: number, y0: number, x1: number, y1: number): Array<[number, number]> {
  const thin = lineTiles(x0, y0, x1, y1);
  if (thin.length < 2) return thin;
  const out: Array<[number, number]> = [thin[0]];
  for (let i = 1; i < thin.length; i++) {
    const [px, py] = thin[i - 1];
    const [cx, cy] = thin[i];
    if (cx !== px && cy !== py) out.push([cx, py]);
    out.push([cx, cy]);
  }
  return out;
}

/**
 * Roads. Cheap, fast, and the closest thing this game has to a city-planning
 * tool: they speed everything that walks on them, which quietly decides where
 * your districts want to be.
 */
function doRoadLine(w: World, x0: number, y0: number, x1: number, y1: number): void {
  for (const [tx, ty] of lineTiles(x0, y0, x1, y1)) {
    if (!w.map.inBounds(tx, ty)) continue;
    const k = w.map.idx(tx, ty);
    if (w.map.terrain[k] === Terrain.Road) continue;
    if (w.map.terrain[k] === Terrain.River) continue;
    if (w.map.building[k] >= 0 || w.map.wall[k] !== WallKind.None || w.map.rubble[k] !== 0) continue;
    if (w.res.stone < 2) break;
    w.res.stone -= 2;
    w.map.terrain[k] = Terrain.Road;
    w.map.terrainVersion++;
    w.dirty.flow = true;
    // A new road can change which yards count as having road access.
    w.dirty.districts = true;
  }
}

/**
 * Raise a stretch of wall by one tier.
 *
 * Per-tile and paid per-tile, stopping the moment the purse runs out, which
 * means a drag across the whole ring upgrades as far as you can afford and
 * leaves the rest standing rather than failing wholesale. Damaged wall keeps
 * its *proportion* of health through the upgrade: rebuilding a battered gate
 * face into a bastion should not be a free full repair.
 */
function doUpgradeWallLine(w: World, x0: number, y0: number, x1: number, y1: number): void {
  const hpMul = w.faction.bonuses.wallHp ?? 1;
  const costMul = w.faction.id === 'karrun' ? 0.8 : w.faction.id === 'moonbound' ? 2 : 1;
  let raised = 0;

  // The same thickened walk the wall was laid with, or a diagonal upgrade would
  // skip exactly the tiles that were inserted to square the corners off.
  for (const [tx, ty] of wallLineTiles(x0, y0, x1, y1)) {
    if (!w.map.inBounds(tx, ty)) continue;
    const k = w.map.idx(tx, ty);
    if (w.map.wall[k] !== WallKind.Wall) continue;
    const tier = w.map.wallTier[k];
    if (tier >= MAX_WALL_TIER) continue;

    const next = wallTierDef(tier + 1);
    const cost: Partial<ResourceBundle> = {};
    for (const key of Object.keys(next.cost) as ResourceKey[]) {
      cost[key] = (next.cost[key] ?? 0) * costMul;
    }
    if (!canAfford(w.res, cost)) break;
    spend(w.res, cost);

    const frac = w.map.wallMaxHp[k] > 0 ? w.map.wallHp[k] / w.map.wallMaxHp[k] : 1;
    w.map.wallTier[k] = tier + 1;
    w.map.wallMaxHp[k] = next.hp * hpMul;
    w.map.wallHp[k] = w.map.wallMaxHp[k] * frac;
    raised++;
    pushEvent(w.events, EV.Build, tx + 0.5, ty + 0.5, 1, 0, Team.Player);
  }

  if (raised > 0) {
    w.dirty.flow = true;
    pushLog(w.events, `${raised} tiles of wall raised.`, LogTone.Good);
  }
}

/**
 * Dig a ditch. Cheap, fast, and it cannot be built on afterwards — a ditch is
 * a commitment about where the ground in front of your line is.
 */
/**
 * Lay planks across deep water.
 *
 * Refuses everything that is not `Terrain.Deep`, and that refusal is the whole
 * design of the tool. A bridge over a ford buys nothing — a ford is already
 * crossable — and a bridge on dry ground is a road, which the player already
 * has. So dragging this anywhere but the channel does nothing and costs
 * nothing, which is a tool that explains the map by refusing.
 */
function doBridgeLine(w: World, x0: number, y0: number, x1: number, y1: number): void {
  let laid = 0;
  for (const [tx, ty] of lineTiles(x0, y0, x1, y1)) {
    if (!w.map.inBounds(tx, ty)) continue;
    const k = w.map.idx(tx, ty);
    if (w.map.terrain[k] !== Terrain.Deep) continue;
    if (w.map.building[k] >= 0 || w.map.wall[k] !== WallKind.None || w.map.rubble[k] !== 0) continue;
    if (!canAfford(w.res, BRIDGE_DEF.cost)) break;
    spend(w.res, BRIDGE_DEF.cost);
    w.map.terrain[k] = Terrain.Bridge;
    laid++;
  }
  if (laid > 0) {
    w.map.terrainVersion++;
    w.dirty.flow = true;
    w.dirty.districts = true;
  }
}

function doMoatLine(w: World, x0: number, y0: number, x1: number, y1: number): void {
  let dug = 0;
  for (const [tx, ty] of lineTiles(x0, y0, x1, y1)) {
    if (!w.map.inBounds(tx, ty)) continue;
    const k = w.map.idx(tx, ty);
    if (w.map.terrain[k] === Terrain.Ditch || w.map.terrain[k] === Terrain.River) continue;
    // Never dig out from under something that is standing on it.
    if (w.map.building[k] >= 0 || w.map.wall[k] !== WallKind.None || w.map.rubble[k] !== 0) continue;
    if (!canAfford(w.res, MOAT_DEF.cost)) break;
    spend(w.res, MOAT_DEF.cost);
    w.map.terrain[k] = Terrain.Ditch;
    dug++;
  }
  if (dug > 0) {
    w.map.terrainVersion++;
    w.dirty.flow = true;
    w.dirty.districts = true;
  }
}

/**
 * The market. Buy at a premium, sell at a discount, and the spread is the
 * merchant's — which is what stops the market from being a money printer that
 * makes the production chain irrelevant.
 *
 * Prices bend with what you are holding: dumping a thousand timber onto a
 * market that is already drowning in it pays less per unit than the first
 * hundred did. That is scarcity pricing rather than a fixed table, and it is
 * what makes a *diversified* economy worth more than a single tall producer.
 */
/**
 * Every tradeable good needs a line here or `doTrade` returns early and the
 * market silently refuses it — which is fine for Souls, and would be a trap for
 * anything the economy is *built on*. The Kindling's four are all priced, and
 * the reason is a deadlock: magewood needs essence, so a settlement whose last
 * Wellspring is rubble on a leyline it no longer holds would have no path back
 * to its own second Age. A market is the path back. It is a bad one — the
 * spread and the glut discount see to that — but it exists.
 *
 * Priced a little above the sum of their inputs, so running the chain always
 * beats buying the output, and selling the output always beats selling the
 * inputs.
 *
 * The same holds all the way up, and it matters more the higher you go: by the
 * Hollow a single converter is four generations of production deep, and losing
 * the ground under any one of them would otherwise end a run that is nominally
 * winning. Note what the prices do to the arithmetic of *buying* your way up,
 * though — hollowglass is two hundred and eighty times timber, so the market is
 * a bridge across a gap in the chain and never a substitute for having one.
 */
const BASE_PRICES: Partial<Record<ResourceKey, number>> = {
  timber: 0.5,
  planks: 1.6,
  stone: 0.9,
  iron: 2.4,
  grain: 0.6,
  bread: 1.5,
  essence: 4.5,
  magewood: 8,
  runestone: 9,
  ingots: 7.5,
  soulwood: 24,
  voidstone: 26,
  alloy: 21,
  quintessence: 32,
  ashglass: 74,
  heartiron: 68,
  hollowglass: 190,
  gravesilver: 176,
  /**
   * The race currencies, and the one deliberate hole in this table.
   *
   * Ichor and Scrap are priced, so a Crimson settlement whose cellars have run
   * dry has the same escape a Kindling settlement with no Wellspring has: an
   * expensive one. Ichor is dear because it is drawn out of people and a market
   * that sold it cheaply would make the whole Thirst optional.
   *
   * **Grudges have no price and that is the design, not an omission.** The
   * standing rule in COORDINATION.md is that a key without a `BASE_PRICES` entry
   * is silently un-tradeable and is therefore a bug waiting to deadlock a chain.
   * Grudges are the exception the rule needs: they are not a good, they are a
   * record of what was done to you, and a Reckoning you could *buy* is not a
   * reckoning. They cannot deadlock anything because nothing but the dwarves'
   * own Academy branch spends them, and that branch is optional by construction.
   * `tests/races.test.ts` pins this down so a later pass cannot "fix" it.
   */
  ichor: 12,
  scrap: 0.8,
  /**
   * The other two foods, priced off what they are worth against the appetite
   * rather than off a feel: fish is 1.4 grain and salt meat is 2 (see
   * `FOOD_VALUE`), so 0.85 and 1.25 sit exactly on grain's 0.6 scaled by their
   * worth. Priced at all because the rule in COORDINATION.md is that an unpriced
   * key is silently un-tradeable and therefore a deadlock waiting to happen —
   * and food is the one deadlock a town cannot wait out.
   */
  fish: 0.85,
  meat: 1.25,
};
const MARKET_SPREAD = 0.28;

export function marketPrice(w: World, key: ResourceKey, selling: boolean): number {
  const base = BASE_PRICES[key];
  if (base === undefined) return 0;
  // Glut discount: the more of it you are sitting on, the less it fetches.
  const glut = 1 / (1 + Math.max(0, w.res[key]) / Math.max(120, w.resCap));
  const price = base * (0.55 + 0.75 * glut);
  return selling ? price * (1 - MARKET_SPREAD) : price * (1 + MARKET_SPREAD);
}

export function hasMarket(w: World): boolean {
  const b = w.buildings;
  for (let i = 0; i < b.slots.high; i++) {
    if (b.slots.alive[i] === 0 || b.build[i] < 1 || b.team[i] !== Team.Player) continue;
    const id = buildingType(b.typeId[i]).id;
    if (id === 'market' || id === 'counting_house') return true;
  }
  return false;
}

/**
 * Commit to the next Age.
 *
 * Validates completely before spending anything — the rule the rubble rebuild
 * taught us — and refuses out loud rather than silently, because a button that
 * does nothing is worse than one that says why.
 */
function doAdvanceAge(w: World): void {
  if (w.battle) return;
  if (w.researchingAge > 0) {
    pushLog(w.events, 'The work is already under way. It will not go faster for being asked.', LogTone.Neutral);
    return;
  }
  const next = w.age + 1;
  if (next > MAX_AGE) {
    pushLog(w.events, 'There is no Age after this one. There is only how long you last in it.', LogTone.Lore);
    return;
  }
  const floor = ageWaveFloor(next);
  if (w.wave < floor) {
    pushLog(w.events, `Too soon. Nothing changes before wave ${floor}, however much you have.`, LogTone.Bad);
    return;
  }
  /**
   * And the Hearth has to be big enough to hold the Age.
   *
   * This is what makes the Hearth's new price a lever on the curve rather than
   * a fee: an Age is bought twice, once in the research bill and once in the
   * stone under the fire, and the second half cannot be skipped by a settlement
   * that simply banked coin. It is a *floor* and not a cost — exactly the shape
   * of the wave floor above it, and for the same reason.
   */
  const tier = w.hearth >= 0 && w.buildings.isAlive(w.hearth) ? w.buildings.tier[w.hearth] : 0;
  const wantTier = ageHearthTier(next);
  if (tier < wantTier) {
    pushLog(
      w.events,
      `The Hearth is too small a fire to carry an Age. Raise it to tier ${wantTier} first.`,
      LogTone.Bad,
    );
    return;
  }
  const cost = ageCost(next);
  if (!canAfford(w.res, cost)) {
    pushLog(w.events, 'Not enough to begin. The Age is not a thing you can be owed.', LogTone.Bad);
    return;
  }

  spend(w.res, cost);
  w.researchingAge = next;
  w.ageResearch = 0;
  pushLog(
    w.events,
    `The work begins on the ${AGE_NAMES[next - 1] ?? 'Hollow'} Age. They will notice, and they will come heavier.`,
    LogTone.Lore,
  );
}

/**
 * Buy the next rank for a unit type.
 *
 * The upgrade is retroactive, and that is the whole point: this is the
 * settlement learning to make better soldiers, so the men already standing in
 * the yard get better too. Health is raised on every living model of that type
 * *in proportion*, so a half-dead veteran comes out of the upgrade still half
 * dead rather than fully healed — a rank is not a heal, and buying one mid-siege
 * must not be a free field hospital.
 */
function doUpgradeUnitRank(w: World, unitId: string): void {
  if (w.battle) return;
  const idx = UNIT_TYPES.findIndex((t) => t.id === unitId);
  if (idx < 0) return;
  const t = UNIT_TYPES[idx];

  const have = w.unitRanks[unitId] ?? 0;
  const next = have + 1;
  if (next > MAX_UNIT_RANK) {
    pushLog(w.events, `${t.name} cannot be drilled any harder.`, LogTone.Neutral);
    return;
  }
  const needAge = rankAgeRequired(t, next);
  if (w.age < needAge) {
    pushLog(w.events, `${t.name} ${RANK_NUMERAL[next]} needs a later Age.`, LogTone.Bad);
    return;
  }
  const cost = rankCost(t, next);
  if (!canAfford(w.res, cost)) {
    pushLog(w.events, `Not enough to drill ${t.name} ${RANK_NUMERAL[next]}.`, LogTone.Bad);
    return;
  }

  spend(w.res, cost);
  w.unitRanks[unitId] = next;

  // Raise what is already standing, proportionally.
  const grow = rankHpMul(next) / rankHpMul(have);
  const u = w.units;
  for (let i = 0; i < u.slots.high; i++) {
    if (u.slots.alive[i] === 0) continue;
    if (u.team[i] !== Team.Player) continue;
    if (u.typeId[i] !== idx) continue;
    const frac = u.maxHp[i] > 0 ? u.hp[i] / u.maxHp[i] : 1;
    u.maxHp[i] *= grow;
    u.hp[i] = u.maxHp[i] * frac;
  }

  pushLog(w.events, `${t.name} ${RANK_NUMERAL[next]}. They will notice the difference.`, LogTone.Good);
}

/** `amount > 0` buys that many units for gold; `amount < 0` sells them. */
function doTrade(w: World, key: string, amount: number): void {
  const k = key as ResourceKey;
  if (!RESOURCE_KEYS.includes(k)) return;
  if (BASE_PRICES[k] === undefined) return;
  /**
   * The second of the mask's four gates, and the one that shuts the back door.
   *
   * Without it a people could simply *buy* a currency it is not supposed to
   * have: the catalogue would never let an Ashen settlement make Ichor and the
   * clamp would delete it every tick, but the coin would already be gone and the
   * player would have watched a bar fill and empty for no stated reason. Refuse
   * at the counter and say so.
   */
  if (!raceEconomyMask(w.raceId, RESOURCE_KEYS).has(k)) {
    pushLog(w.events, `No one here deals in ${RESOURCE_LABELS[k]}.`, LogTone.Bad);
    return;
  }
  if (!hasMarket(w)) {
    pushLog(w.events, 'Nowhere to trade. Build a marketplace.', LogTone.Bad);
    return;
  }
  const n = Math.round(amount);
  if (n === 0) return;

  if (n > 0) {
    const unit = marketPrice(w, k, false);
    /**
     * Buy only what there is room to keep.
     *
     * Commands apply at the top of the tick and `stepSettlement` clamps to
     * `resCap` later in that same tick, so anything bought over the cap was
     * charged for and then quietly evaporated before it could be spent — a
     * near-full granary would take your gold and give you nothing. The sell
     * path already refused to sell what you do not have; this is the missing
     * half of that symmetry.
     */
    const room = storageRoom(w, k);
    const affordable = Math.min(n, Math.floor(w.res.gold / Math.max(1e-6, unit)), Math.floor(room));
    if (affordable <= 0) {
      if (room < 1) pushLog(w.events, 'Nowhere to put it. Build more storage.', LogTone.Bad);
      return;
    }
    w.res.gold -= affordable * unit;
    w.res[k] += affordable;
  } else {
    const want = Math.min(-n, Math.floor(w.res[k]));
    if (want <= 0) return;
    const unit = marketPrice(w, k, true);
    w.res[k] -= want;
    w.res.gold += want * unit;
  }
  if (w.res.gold < 0) w.res.gold = 0;
}

function doClearRubble(w: World, tx: number, ty: number): void {
  if (!w.map.inBounds(tx, ty)) return;
  const k = w.map.idx(tx, ty);
  if (w.map.rubble[k] === 0) return;
  // Ordering a clear just marks it urgent; villagers still have to do the work.
  w.map.rubbleWork[k] = Math.min(w.map.rubbleWork[k], RUBBLE_CLEAR_SECONDS * 0.5);
  w.dirty.jobs = true;
}

/**
 * Is this a footprint the rebuild may legally take?
 *
 * Every tile has to be on the map, clear of buildings, and carrying *this*
 * building's rubble mark — the whole outline, every tile of it. Two things were
 * wrong before, and one rule fixes both.
 *
 * The check only ever asked `building[kk] >= 0`, so a farm's pile lying inside
 * a hovel's outline was dissolved along with the hovel's: no salvage, no cost,
 * and none of the villager-time that clearing it honestly would have taken.
 *
 * And accepting bare ground inside the outline let the whole footprint drift.
 * Rubble records a type, not an origin, so the only evidence of where a
 * building stood is the shape of the pile it left; treat a tile of open ground
 * as acceptable and the rebuild will happily slide a hovel a row north onto
 * land it never occupied, clearing two tiles of somebody's rubble on the way
 * past. Requiring the full mark costs the player a one-click rebuild on a pile
 * that has already been part-carted-away, which is the correct answer: once the
 * outline is gone there is nothing to put back, and the ordinary build button
 * is right there.
 */
function rubbleFootprintOk(
  w: World,
  ox: number,
  oy: number,
  def: { w: number; h: number },
  mark: number,
): boolean {
  for (let dy = 0; dy < def.h; dy++) {
    for (let dx = 0; dx < def.w; dx++) {
      const x = ox + dx;
      const y = oy + dy;
      if (!w.map.inBounds(x, y)) return false;
      const kk = w.map.idx(x, y);
      if (w.map.building[kk] >= 0) return false;
      if (w.map.rubble[kk] !== mark) return false;
    }
  }
  return true;
}

/**
 * Which building's outline the clicked tile belongs to.
 *
 * Rubble records a *type*, not a footprint, so two hovels that came down side
 * by side leave one four-wide field of identical marks. Walking back to the
 * first non-matching tile therefore merged the pair and rebuilt the left-hand
 * one wherever you clicked — wrong rather than fatal, since a second click put
 * the other one up, but wrong.
 *
 * So: find the field's top-left corner as before, and then step *in footprint
 * strides* from it. A row of two hovels tiles cleanly into two hovels, and the
 * clicked tile falls inside exactly one of them. Where the field is an odd
 * shape the stride cannot land on, fall back to every origin that could contain
 * the click; `rubbleFootprintOk` is strict enough that none of those can drift
 * off the pile. If nothing is legal the command refuses outright and touches
 * nothing, which is the rule this function taught the codebase.
 */
function rubbleOrigin(
  w: World,
  tx: number,
  ty: number,
  def: { w: number; h: number },
  mark: number,
): { ox: number; oy: number } | null {
  let minX = tx;
  let minY = ty;
  while (minX > 0 && w.map.rubble[w.map.idx(minX - 1, ty)] === mark) minX--;
  while (minY > 0 && w.map.rubble[w.map.idx(tx, minY - 1)] === mark) minY--;

  const ox = minX + Math.floor((tx - minX) / def.w) * def.w;
  const oy = minY + Math.floor((ty - minY) / def.h) * def.h;
  if (rubbleFootprintOk(w, ox, oy, def, mark)) return { ox, oy };

  for (let x = tx; x > tx - def.w; x--) {
    for (let y = ty; y > ty - def.h; y--) {
      if (rubbleFootprintOk(w, x, y, def, mark)) return { ox: x, oy: y };
    }
  }
  return null;
}

/**
 * Would a rebuild here work, and if not, why not?
 *
 * The reader half of `Cmd.RebuildRubble`, and it exists for the reason
 * `buildRefusal` exists: a rubble tile the player is hovering over is either a
 * one-click rebuild or it is not, and the two need to be the *same* question
 * asked twice — once to grey a button and print a sentence, once at the moment
 * of the click — or the button and the command will drift.
 *
 * `''` means it would go ahead. Everything else is a sentence, not a boolean,
 * because a boolean teaches nobody anything — and the last of them is the one
 * that used to be a mystery: rubble records a *type*, not an origin, so once a
 * pile has been part-carted-away there is no longer an outline to put anything
 * back on, and the ordinary build button is the answer.
 */
export function rebuildRefusal(w: World, tx: number, ty: number): string {
  if (!w.map.inBounds(tx, ty)) return 'Off the map.';
  const mark = w.map.rubble[w.map.idx(tx, ty)];
  if (mark === 0) return 'Nothing stood here.';
  const typeIdx = mark - 1;
  const def = buildingType(typeIdx);
  const standing = buildRefusal(w, typeIdx);
  if (standing !== '') return standing;
  if (!canAfford(w.res, buildCostFor(w, typeIdx))) return 'Not enough materials.';
  if (!rubbleOrigin(w, tx, ty, def, mark)) return 'Too much of it has been carted away.';
  return '';
}

/** What the rubble under a tile remembers, for a panel. `null` if it is clear. */
export function rubbleReadout(
  w: World,
  tx: number,
  ty: number,
): { typeIdx: number; name: string; cost: Partial<ResourceBundle>; refusal: string } | null {
  if (!w.map.inBounds(tx, ty)) return null;
  const mark = w.map.rubble[w.map.idx(tx, ty)];
  if (mark === 0) return null;
  const typeIdx = mark - 1;
  return {
    typeIdx,
    name: buildingType(typeIdx).name,
    cost: buildCostFor(w, typeIdx),
    refusal: rebuildRefusal(w, tx, ty),
  };
}

/**
 * Put a crew on this building now, or call them off.
 *
 * The order is a standing intent rather than an instantaneous act, which is why
 * it is a list on the world rather than a write to the building: masons have to
 * walk, and `assignJobs` is what sends them. It is refused for anything that is
 * not a finished, living, player-owned and actually-damaged building, so the
 * list cannot fill with entries that will never come off it.
 */
function doOrderRepair(w: World, idx: number, cancel: boolean): void {
  const at = w.repairOrders.indexOf(idx);
  if (cancel) {
    if (at >= 0) w.repairOrders.splice(at, 1);
    return;
  }
  const b = w.buildings;
  if (!b.isAlive(idx) || b.team[idx] !== Team.Player || b.build[idx] < 1) return;
  if (b.hp[idx] >= b.maxHp[idx] - 0.5) {
    pushLog(w.events, 'There is nothing wrong with it.', LogTone.Neutral);
    return;
  }
  if (at >= 0) return;
  w.repairOrders.push(idx);
  w.dirty.jobs = true;
  pushLog(w.events, `Masons to the ${buildingType(b.typeId[idx]).name}. Now, not after.`, LogTone.Neutral);
}

/**
 * The harness's door onto depletion: take the wood down over an area.
 *
 * It exists because the alternative is a test that reaches into `w.nodes` and
 * empties a patch by hand, which would be testing a world no player and no
 * replay can reach. Everything the simulation does to a node goes through the
 * same two functions this calls, so a stand cleared from the console leaves the
 * map in exactly the state forty minutes of woodcutting would have left it in —
 * ground reverted, flow weights dirty, terrain re-baked.
 */
function doClearNodes(w: World, tx: number, ty: number, radius: number, kindId: string): void {
  const kind = kindId ? nodeKindById(kindId) : -1;
  if (kindId && kind < 0) return;
  const r = Math.max(0, Math.round(radius));
  const r2 = r * r;
  let felled = 0;
  let ground = false;
  const n = w.nodes;
  for (let i = n.slots.high - 1; i >= 0; i--) {
    if (n.slots.alive[i] === 0) continue;
    if (kind >= 0 && n.kind[i] !== kind) continue;
    const t = n.tile[i];
    const dx = (t % w.map.w) - tx;
    const dy = ((t / w.map.w) | 0) - ty;
    if (dx * dx + dy * dy > r2) continue;
    if (removeNode(w.map, w.nodes, i)) ground = true;
    felled++;
  }
  if (ground) w.dirty.flowWeights = true;
  if (felled > 0) pushLog(w.events, `${felled} taken off the ground.`, LogTone.Neutral);
}

/** One-click "put it back": clears the footprint and re-places what stood there. */
function doRebuildRubble(w: World, tx: number, ty: number): void {
  if (!w.map.inBounds(tx, ty)) return;
  const k = w.map.idx(tx, ty);
  const mark = w.map.rubble[k];
  if (mark === 0) return;
  const typeIdx = mark - 1;
  const def = buildingType(typeIdx);

  if (!canAfford(w.res, buildCostFor(w, typeIdx))) return;

  /**
   * Validate completely before mutating anything.
   *
   * This used to validate and mutate in the same pass, so a footprint that
   * failed on its second tile had already wiped the first — and then returned
   * without spending, without building and without a word. Rubble beside a
   * surviving building could be dissolved a tile per click, free, when clearing
   * it properly costs a villager real time.
   */
  const origin = rubbleOrigin(w, tx, ty, def, mark);
  if (!origin) return;
  const { ox, oy } = origin;

  for (let dy = 0; dy < def.h; dy++) {
    for (let dx = 0; dx < def.w; dx++) {
      const kk = w.map.idx(ox + dx, oy + dy);
      if (w.map.rubble[kk] !== 0) w.rubbleCount = Math.max(0, w.rubbleCount - 1);
      w.map.rubble[kk] = 0;
      w.map.rubbleWork[kk] = 0;
    }
  }
  spend(w.res, buildCostFor(w, typeIdx));
  placeBuilding(w, typeIdx, ox, oy, Team.Player, false);
  pushLog(w.events, `${def.name} goes back up. Again.`, LogTone.Neutral);
}

/**
 * What raising this building one tier costs, right now.
 *
 * Exported because the build panel used to hold a copy of this arithmetic and a
 * comment saying the simulation owned the price, which is the shape of a rule
 * that drifts. The panel asks now.
 *
 * `upgradeCost` overrides `cost` where a building has one, and exactly one does:
 * **the Hearth, which is placed for free and so used to be upgraded for free.**
 * Four tiers of beds, storage and stone for nothing — the owner's *"hearth
 * upgrade should not be free"* was not a missing price so much as a price
 * derived from a building nobody ever buys.
 */
export function upgradeCostFor(w: World, idx: number): Partial<ResourceBundle> {
  const b = w.buildings;
  const def = buildingType(b.typeId[idx]);
  const base = def.upgradeCost ?? def.cost;
  const cost: Partial<ResourceBundle> = {};
  const scale = 0.85 * b.tier[idx];
  for (const k of Object.keys(base) as Array<keyof ResourceBundle>) {
    cost[k] = Math.round((base[k] ?? 0) * scale) + 10;
  }
  return cost;
}

function doUpgradeBuilding(w: World, idx: number): void {
  const b = w.buildings;
  if (!b.isAlive(idx) || b.team[idx] !== Team.Player || b.build[idx] < 1) return;
  const def = buildingType(b.typeId[idx]);
  const maxTier = Math.max(def.maxTier, w.faction.bonuses.maxBuildingTier ?? 3);
  if (b.tier[idx] >= maxTier) return;

  const cost = upgradeCostFor(w, idx);
  if (!canAfford(w.res, cost)) return;
  spend(w.res, cost);
  b.tier[idx]++;
  b.maxHp[idx] = def.hp * (1 + (b.tier[idx] - 1) * 0.55);
  b.hp[idx] = b.maxHp[idx];
  recomputePopCap(w);
  recomputeStorage(w);
  // An upgraded Ossuary is a wider pool, on the same tier curve `popCap` uses.
  recomputeManaCap(w);
  pushLog(w.events, `${def.name} raised to tier ${b.tier[idx]}.`, LogTone.Good);
}

function doRecruit(w: World, typeIdx: number): void {
  const t = unitType(typeIdx);
  if (t.tier > w.age) return;
  if (!w.faction.roster.includes(t.id)) return;
  // Must own a building that trains this kind of soldier.
  if (!hasTrainer(w, typeIdx)) {
    pushLog(w.events, `No one here can drill ${t.name}.`, LogTone.Bad);
    return;
  }
  const x = w.map.hearthX + 0.5;
  const y = w.map.hearthY + 3.5;
  recruitCompany(w, typeIdx, x, y);
}

export function trainerCategoryFor(typeIdx: number): string {
  const t = unitType(typeIdx);
  if ((t.flags & 32) !== 0 /* UF.Ranged */) return 'archer';
  if ((t.flags & 8) !== 0 /* UF.Cavalry */) return 'cavalry';
  if ((t.flags & 1024) !== 0 /* UF.Caster */) return 'caster';
  if ((t.flags & 2) !== 0 /* UF.Siege */) return 'siege';
  return 'militia';
}

export function hasTrainer(w: World, typeIdx: number): boolean {
  const want = trainerCategoryFor(typeIdx);
  const b = w.buildings;
  for (let i = 0; i < b.slots.high; i++) {
    if (b.slots.alive[i] === 0) continue;
    if (b.build[i] < 1 || b.team[i] !== Team.Player) continue;
    const def = buildingType(b.typeId[i]);
    if (def.trains.includes(want)) return true;
    if (want === 'militia' && def.trains.includes('spear')) return true;
    if (want === 'archer' && def.trains.includes('crossbow')) return true;
  }
  return false;
}

function doUpgradePower(w: World, id: string): void {
  const def = POWERS.find((p) => p.id === id);
  if (!def) return;
  if (!w.unlockedPowers.includes(id)) return;
  const tier = w.powerTiers[id] ?? 0;
  if (tier >= 3) return;
  const cost = def.tiers[tier].soulCost;
  if (w.res.souls < cost) return;
  w.res.souls -= cost;
  w.powerTiers[id] = tier + 1;
  pushLog(w.events, `${def.name} deepens. ${def.tiers[tier].text}`, LogTone.Lore);
}

function doPickRelic(w: World, id: string): void {
  const relic = RELICS.find((r) => r.id === id);
  if (!relic) return;
  if (w.relics.includes(id)) return;
  w.relics.push(id);
  foldPermanent(w, () => relic.apply(w));
  pushLog(w.events, `${relic.name} is yours. ${relic.rules}`, LogTone.Lore);
  pushEvent(w.events, EV.Levelup, w.map.hearthX, w.map.hearthY, 9, 0, Team.Player, 0, 0, id);
}

/**
 * Answer the Chronicle.
 *
 * `s` names the event and `a` indexes the offered choices — the *offered* list,
 * frozen into `w.pendingEvent` at draw time, not the deck order. Naming the
 * event as well as the index is what stops a stale command from a previous
 * modal answering the one currently on screen with whatever index it happened
 * to carry.
 *
 * The world unfreezes the instant this returns, on the same tick, so answering
 * costs the player nothing in time.
 */
function doResolveEvent(w: World, eventId: string, index: number): void {
  const p = w.pendingEvent;
  if (!p) return;
  if (eventId && eventId !== p.id) return;

  const def = chronicleEvent(p.id);
  if (!def) {
    // The deck changed under a save. Drop it rather than freeze forever.
    w.pendingEvent = null;
    return;
  }

  const i = Math.max(0, Math.min(p.choices.length - 1, Math.round(index)));
  const choice = def.choices.find((c) => c.id === p.choices[i]);
  if (!choice) {
    w.pendingEvent = null;
    return;
  }
  // Refused rather than forgiven: the modal stays up and the player picks again.
  // Every event carries at least one costless choice, so this cannot deadlock —
  // `tests/events.test.ts` holds the deck to that.
  if (choice.cost && !canAfford(w.res, choice.cost)) return;

  w.pendingEvent = null;
  w.eventHistory.push(`${def.id}>${choice.id}`);
  if (choice.cost) spend(w.res, choice.cost);
  const line = foldPermanent(w, () => choice.apply(w));
  pushLog(w.events, line, choice.tone ?? LogTone.Neutral);
  pushEvent(w.events, EV.Levelup, w.map.hearthX, w.map.hearthY, 4, 0, Team.Player, 0, 0, def.id);
}

/**
 * The highest tier of finished Academy standing, or 0 for none.
 *
 * A tier rather than a count on purpose: two Academies are not twice the
 * research, they are two buildings. What a settlement buys when it upgrades is
 * *depth*, and depth is the thing the upper Ages were short of.
 */
export function academyTier(w: World): number {
  let best = 0;
  const b = w.buildings;
  for (let i = 0; i < b.slots.high; i++) {
    if (b.slots.alive[i] === 0) continue;
    if (b.build[i] < 1 || b.team[i] !== Team.Player) continue;
    if (buildingType(b.typeId[i]).id !== 'academy') continue;
    if (b.tier[i] > best) best = b.tier[i];
  }
  return best;
}

/** Which Academy tier a node of this Age needs. 1–2 → I, 3–4 → II, 5 → III. */
export function academyTierFor(age: number): number {
  return Math.max(1, Math.ceil(Math.max(1, age) / 2));
}

/**
 * Why this node cannot be bought, or '' if it can.
 *
 * Exported because the research panel has to be able to grey a node *and say
 * why*: the whole value of gating tech behind a building is lost if the button
 * simply stops working. Every refusal below returns a sentence.
 */
export function researchError(w: World, id: string): string {
  const node = techNode(id);
  if (!node) return 'No such study.';
  if (w.techNodes.includes(id)) return 'Already known.';
  if (node.race && node.race !== w.raceId) return 'Not ours to learn.';
  if (node.requires.some((r) => !w.techNodes.includes(r))) return 'Something else comes first.';
  if (node.age > w.age) return 'The Age is not yet upon us.';
  const have = academyTier(w);
  if (have === 0) return 'There is nowhere to study. Raise an Academy.';
  const want = academyTierFor(node.age);
  if (have < want) return `The Academy is not deep enough. It wants tier ${want}.`;
  if (!canAfford(w.res, node.cost)) return 'We cannot pay for it.';
  return '';
}

function doResearch(w: World, id: string): void {
  const node = techNode(id);
  if (!node) return;
  /**
   * The Academy gate.
   *
   * Tech used to be bought straight off a menu the moment its Age opened, which
   * made the sixty-node tree a shopping list rather than something you build
   * toward — and left the one obvious question the owner asked, *"did you do an
   * academy of sort to be able to research"*, with the honest answer of no.
   *
   * The gate is deliberately a building rather than a resource: a resource cost
   * is a fifth number on a line that already has four, and the settlement layer
   * is where decisions are supposed to be *placed*. An Academy takes ground,
   * takes builders, can be knocked down in a siege, and is upgraded with the
   * goods of the Age it is trying to see past.
   *
   * Note what did **not** change: nothing was taken away. Every one of the
   * sixty shared nodes is still buyable by every people at the same Age for the
   * same price. What is new is that all of them now sit behind one building, and
   * that each race has a fourth column beside them that nobody else can see.
   */
  const err = researchError(w, id);
  if (err !== '') {
    if (w.techNodes.includes(id) || !canAfford(w.res, node.cost)) return;
    pushLog(w.events, err, LogTone.Bad);
    return;
  }
  if (!canAfford(w.res, node.cost)) return;
  spend(w.res, node.cost);
  w.techNodes.push(id);
  foldPermanent(w, () => node.apply(w));
  statsOf(w).buildingsBuilt += 0;
  pushLog(w.events, `${node.name}. ${node.rules}`, LogTone.Good);
}

/**
 * A hero's battle skill.
 *
 * The same payload a Sovereign Power delivers, bought differently: no Will, no
 * shared Power cooldown, but it has to land within reach of where the hero is
 * actually standing. That reach is the whole design — a Sovereign casts from
 * above the map, a hero casts from inside it, so *where he is* becomes a
 * decision rather than a detail.
 */
function doHeroAbility(w: World, id: string, x: number, y: number, dirX: number, dirY: number): void {
  if (heroAbilityError(w, id) !== '') return;
  const i = heroUnit(w);
  if (i < 0) return;
  if (Math.hypot(x - w.units.x[i], y - w.units.y[i]) > HERO_ABILITY_RANGE) return;

  const cooldown = unleashPower(w, id, x, y, dirX, dirY);
  if (cooldown <= 0) return;
  w.hero.cooldowns[id] = cooldown * HERO_ABILITY_COOLDOWN_MUL;
  pushLog(w.events, `${w.hero.name}: ${power(id).name}.`, LogTone.Lore);
}

/** Pick a company up and put it down somewhere else, formation intact. */
function teleportCompany(w: World, c: Company, x: number, y: number): void {
  const dx = x - c.ax;
  const dy = y - c.ay;
  c.ax = x;
  c.ay = y;
  c.orderX = x;
  c.orderY = y;
  for (const h of c.members) {
    const i = w.units.resolve(h);
    if (i < 0) continue;
    w.units.x[i] += dx;
    w.units.y[i] += dy;
    w.units.goalX[i] = w.units.x[i];
    w.units.goalY[i] = w.units.y[i];
    w.units.vx[i] = 0;
    w.units.vy[i] = 0;
  }
}

/** Everything the player is currently allowed to place. */
export function availableBuildings(w: World): number[] {
  const out: number[] = [];
  for (let i = 0; i < BUILDING_TYPES.length; i++) {
    const def = BUILDING_TYPES[i];
    if (def.category === 'core') continue;
    if (def.tier > w.age) continue;
    /**
     * The tech gate, read off the catalogue rather than special-cased here.
     * The special case this replaces checked `'sacrament_furnace'` — a node id
     * that has never existed (`sac_furnace` is the node) — so the one building
     * that node claimed to unlock was silently unbuildable for everyone, and
     * no test could catch a string that named nothing. The field is data, and
     * `tests/tech.test.ts` walks every gate against the real tree.
     */
    if (def.tech && !w.techNodes.includes(def.tech)) continue;
    out.push(i);
  }
  return out;
}

export function availablePowers(w: World): string[] {
  return w.unlockedPowers.slice();
}

export function powerById(id: string) {
  return power(id);
}

export function availableTech(w: World) {
  return TECH_NODES.filter((n) => n.age <= w.age && !w.techNodes.includes(n.id));
}
